import { Link } from 'react-router-dom';
import { useSwipeNavigateOnMobile } from '@/hooks/useSwipeNavigateOnMobile';
import { SwipeRevealTradingCardShell } from '@/components/trading/SwipeRevealTradingCardShell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChartArea, faRocket, faUnlock, faCircleCheck, faTriangleExclamation, faCircleXmark, faCircle, faChevronDown, faChevronUp } from '@/fontawesome';
import MinimalPriceChart from '@/components/ui/MinimalPriceChart';
import Tooltip from '@/components/ui/Tooltip';
import FavoriteControl from '@/components/trading/FavoriteControl';
import { formatCurrencyCentValue, calculatePremiumPercentage, formatDate, calculateOpenStatus } from '@/utils/helpers';
import { getDetailLinkForRecommendation, getStatusBorderColor } from '@/utils/trading-recommendation-helpers';
import type { RecommendationCore, RecommendationEarnings, RecommendationFavorite } from '@/models';
import type { GraphPoint } from '@/modelsAPI';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';
import {faSquareCheck} from "@fortawesome/free-solid-svg-icons";

type AnyRecommendation = RecommendationCore | RecommendationEarnings;

type RecommendationWithGraph = AnyRecommendation & {
  graphPoints?: GraphPoint[];
  graphLoading?: boolean;
};

interface TradingRecommendationCardProps {
  recommendation: RecommendationWithGraph;
  recommendationType: TradingRecommendationType;
  expanded?: boolean;
  onToggle?: () => void;
  onFavoriteChange?: (favorite: RecommendationFavorite | null) => void;
}

export default function TradingRecommendationCard({ recommendation, recommendationType, expanded = false, onToggle, onFavoriteChange }: TradingRecommendationCardProps) {
  const detailLink = getDetailLinkForRecommendation(recommendationType, recommendation.id);
  const swipeRef = useSwipeNavigateOnMobile(detailLink);

  return (
    <SwipeRevealTradingCardShell
      swipeRef={swipeRef}
      surfaceAriaLabel="Zum Detail nach rechts wischen; auf größeren Bildschirmen Chart anklicken"
      surfaceClassName={`touch-pan-y bg-white dark:bg-gray-800 rounded-lg p-3 lg:p-6 border-r-4 ${getStatusBorderColor(recommendation)} ${
      recommendation.boost
        ? 'border-2 border-amber-400 dark:border-amber-500 shadow-lg shadow-amber-200/50 dark:shadow-amber-900/30'
        : 'border border-gray-200 dark:border-gray-700'
    }`}
    >
      {/* Favorite control (top right) */}
      <FavoriteControl
        type={recommendationType}
        recommendationId={recommendation.id}
        favorite={recommendation.favorite}
        onChange={(fav) => onFavoriteChange?.(fav)}
        className="absolute top-2 right-2 z-20"
      />

      <div className="items-start gap-2 lg:gap-3 grid sm:grid md:grid lg:flex pr-16">
        {/* Chart - hidden on mobile, fixed width on large screens */}
        <div className="hidden lg:block lg:w-[180px] lg:flex-none">
          <div className="w-full h-20">
            {recommendation.graphLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-gray-400 animate-spin" />
              </div>
            ) : recommendation.graphPoints && recommendation.graphPoints.length > 0 ? (
              <Link to={detailLink} className="block cursor-pointer">
                <MinimalPriceChart
                  points={recommendation.graphPoints}
                  strikePrice={recommendation.strikePrice / 100}
                  height={80}
                />
              </Link>
            ) : (
              <Link to={detailLink} className="block cursor-pointer w-full h-full">
                <div className="w-full h-full bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                  <FontAwesomeIcon
                    icon={faChartArea}
                    className="w-8 h-8 text-gray-400 dark:text-gray-500"
                  />
                </div>
              </Link>
            )}
          </div>
        </div>

        {/* Details container - fills remaining space on large screens */}
        <div className="flex-1 grid gap-3 grid-cols-3 lg:grid-cols-16">
          {/* Symbol with Chevron */}
          <div className="col-span-1 lg:col-span-2 min-w-0">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Symbol</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 min-w-0">
              <span className="inline-flex flex-wrap min-w-0">
                {recommendation.stockExchange?.identifier !== null && recommendation.stockExchange?.identifier !== undefined
                  ? <span>{recommendation.stockExchange.identifier}:</span>
                  : null}
                <span>{recommendation.symbolTicker}</span>
              </span>
              {/* Chevron for Mobile Expand/Collapse */}
              {onToggle && (
                <button
                  onClick={onToggle}
                  className="lg:hidden shrink-0 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer transition-colors"
                  aria-label={expanded ? "Einklappen" : "Ausklappen"}
                >
                  <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    className="w-4 h-4"
                  />
                </button>
              )}
            </div>
          </div>

          {/* Badges - only visible when expanded on mobile */}
          {(recommendation.boost || recommendation.free) && (
            <div className={`col-span-3 ${expanded ? 'flex' : 'hidden'} lg:hidden flex-wrap gap-1 mt-1`}>
              {recommendation.boost && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                  <FontAwesomeIcon icon={faRocket} className="w-3 h-3 mr-1" />
                  BOOST
                </span>
              )}
              {recommendation.free && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                  <FontAwesomeIcon icon={faUnlock} className="w-3 h-3 mr-1" />
                  FREE
                </span>
              )}
            </div>
          )}

          {/* Sector - hidden on mobile */}
          <div className="hidden lg:block lg:col-span-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Branche</div>
            <div className="text-base text-gray-900 dark:text-white">
              <span
                className="block max-w-[260px] truncate"
                title={recommendation.symbol?.sector ?? ''}
              >
                {recommendation.symbol?.sector ?? '-'}
              </span>
            </div>
          </div>

          {/* Strike Price */}
          <div className="col-span-1 lg:col-span-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Strike</div>
            <div className="text-base text-gray-900 dark:text-white text-left tabular-nums">
              {formatCurrencyCentValue(recommendation.strikePrice, recommendation.symbol?.currentData?.currency || 'USD')}
            </div>
          </div>

          {/* Prämie */}
          <div className="col-span-1 lg:col-span-4">
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Prämie</div>
            <div className="text-base text-gray-900 dark:text-white text-left tabular-nums">
              {formatCurrencyCentValue(recommendation.premium, recommendation.symbol?.currentData?.currency ?? 'USD')}
            </div>
            {calculatePremiumPercentage(recommendation.premium, recommendation.strikePrice) !== null && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                ({(calculatePremiumPercentage(recommendation.premium, recommendation.strikePrice)! * 100).toFixed(2)}%)
              </div>
            )}
          </div>

          {/* Start Date - mobile: conditional, desktop: always visible */}
          <div className={`col-span-1 lg:col-span-2 ${expanded ? 'block' : 'hidden'} lg:block`}>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Startdatum</div>
            <div className="text-base text-gray-900 dark:text-white text-left tabular-nums">
              {formatDate(recommendation.startDate).split(' ')[0].replaceAll(',','')}
            </div>
          </div>

          {/* End Date - mobile: conditional, desktop: always visible */}
          <div className={`${expanded ? 'block' : 'hidden'} lg:block lg:col-span-2`}>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Enddatum</div>
            <div className="text-base text-gray-900 dark:text-white text-left tabular-nums">
              {formatDate(recommendation.endDate).split(' ')[0].replaceAll(',','')}
            </div>
          </div>

          {/* Status - mobile: conditional, desktop: always visible */}
          <div className={`col-span-1 lg:col-span-2 ${expanded ? 'block' : 'hidden'} lg:block`}>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Status</div>
            {recommendation.state === 'OPEN' ? (
              (() => {
                const openStatus = calculateOpenStatus(recommendation.winLossDelta);

                if (openStatus === null) {
                  return (
                    <Tooltip text="Kein winLossDelta verfügbar">
                      <FontAwesomeIcon icon={faCircle} className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                    </Tooltip>
                  );
                }

                if (openStatus === 'GEHT_DURCH') {
                  return (
                    <Tooltip text="Geht durch">
                      <FontAwesomeIcon icon={faCircleCheck} className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </Tooltip>
                  );
                } else if (openStatus === 'WIRD_KNAPP') {
                  return (
                    <Tooltip text="Wird knapp">
                      <FontAwesomeIcon icon={faTriangleExclamation} className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                    </Tooltip>
                  );
                } else {
                  return (
                    <Tooltip text="Wird eingebucht">
                      <FontAwesomeIcon icon={faCircleXmark} className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </Tooltip>
                  );
                }
              })()
            ) : (
              <>
                {recommendation.state === 'FINISHED_ASSIGNED' ? (
                  <Tooltip text="Eingebucht">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                  </Tooltip>
                ) : (
                  <Tooltip text="Erfolgreich beendet">
                    <FontAwesomeIcon icon={faSquareCheck} className="w-6 h-6 text-lime-400 dark:text-lime-200" />
                  </Tooltip>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </SwipeRevealTradingCardShell>
  );
}
